/* REACT */
import { ReactNode } from 'react';

/* ROUTER */
import { Link } from 'react-router-dom';

/* PLUGINS */
import { ArrowLeft } from 'lucide-react';

/* COMPONENTS */
/**
 * DOCU: Props for AuthLayout component. <br>
 * Last Updated Date: December 1, 2025 <br>
 * @author Kirt, Updated by: Sam
 */
interface AuthLayoutProps {
    /** Form content to render inside the card */
    children: ReactNode;
    /** Page title displayed in the card header */
    title: string;
    /** Whether to show back to login button (default: false) */
    showBackButton?: boolean;
}

/**
 * DOCU: Shared authentication layout wrapper with logo, card container, and footer. <br>
 * Triggered: Used in login and registration pages for consistent layout. <br>
 * Last Updated Date: December 1, 2025 <br>
 * @param children - Form content to render inside the card.
 * @param title - Page title displayed in the card header.
 * @param showBackButton - Whether to show back to login button.
 * @author Kirt, Updated by: Sam
 *
 * @example
 * ```tsx
 * <AuthLayout title="Sign In">
 *   <LoginFormContent />
 * </AuthLayout>
 *
 * <AuthLayout title="Create Account" showBackButton>
 *   <RegistrationFormContent />
 * </AuthLayout>
 * ```
 */
export function AuthLayout({ children, title, showBackButton = false }: AuthLayoutProps) {
    return (
        <div className="min-h-screen bg-[#f7f7f7] flex items-center justify-center px-6 py-8">
            <div className="w-full max-w-[480px]">
                {/* Back Button */}
                {showBackButton && (
                    <Link
                        to="/login"
                        className="text-[#525252] mb-4 flex items-center gap-2 hover:text-[#171717] transition-colors"
                        aria-label="Go back to login page"
                    >
                        <ArrowLeft className="w-5 h-5" aria-hidden="true" />
                        Back to Login
                    </Link>
                )}

                {title && (
                    <div className="rounded-lg border border-[#e5e5e5] bg-white p-8">
                        <h2 className="text-[#363636] mb-6">{title}</h2>
                        {children}
                    </div>
                )}

                {!title && children}

                {/* Footer */}
                <footer className="text-center mt-6">
                    <p className="text-[#737373] text-sm">
                        © 2025 IDL Projects. All rights reserved.
                    </p>
                </footer>
            </div>
        </div>
    );
}
